import { Link } from '@inertiajs/react'
import { GraduationCap, MessageCircle, PlayCircle, Star, TrendingUp } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { StatCard } from '@/components/cards/StatCard'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { buttonClasses } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type DashboardProps = {
  stats: {
    profileCompletion: number
    chatroomsCount: number
    mediaEntries: number
    approvalStatus: string
  }
  recentChatrooms: Array<{
    id: string
    schoolName: string | null
    coachName: string
    lastMessage: string | null
    lastMessageAuthor: string | null
    lastMessageAt: string | null
  }>
  profileInsights: {
    status: string
    missingFields: string[]
    suggestions: string[]
  }
  latestRejectedRequest?: {
    id: string
    rejectionReason?: string | null
    reviewedAt?: string | null
  } | null
  breadcrumbs: Breadcrumb[]
}

export default function PlayerDashboard({ stats, recentChatrooms, profileInsights, latestRejectedRequest, breadcrumbs }: DashboardProps) {
  const approvalBadge = badgeForStatus(profileInsights.status)

  return (
    <AppShell>
      <PageHeader
        title="選手ダッシュボード"
        description="進捗状況を確認し、スカウトとのやりとりを次のアクションへ繋げましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex flex-wrap gap-3">
            <Link href="/player/academic_grades" className={buttonClasses({ variant: 'secondary' })}>
              <GraduationCap className="h-4 w-4" />
              成績を入力
            </Link>
            <Link href="/player/profile/edit" className={buttonClasses({})}>
              プロフィールを更新
            </Link>
          </div>
        }
      />

      {latestRejectedRequest ? (
        <Card className="border-amber-300/30 bg-amber-300/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-amber-50">プロフィールが差し戻されました</h2>
              <p className="text-sm text-amber-50/80 whitespace-pre-wrap">
                {latestRejectedRequest.rejectionReason || 'チームコーチからプロフィールへの追記が必要と判断されました。'}
              </p>
              {latestRejectedRequest.reviewedAt ? (
                <p className="text-xs text-amber-50/60">{formatDate(latestRejectedRequest.reviewedAt)} 確認</p>
              ) : null}
            </div>
            <Link href="/player/profile/edit" className={buttonClasses({ variant: 'secondary' })}>
              プロフィールを修正する
            </Link>
          </div>
        </Card>
      ) : null}

      <section className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="プロフィール完成度"
          value={`${stats.profileCompletion}%`}
          delta={{
            value: profileInsights.missingFields.length === 0 ? 'すべての項目が入力済み' : `${profileInsights.missingFields.length}項目が未入力`,
            trend: profileInsights.missingFields.length === 0 ? 'up' : 'steady',
          }}
          icon={<TrendingUp className="h-5 w-5" />}
        />
        <StatCard
          label="チャットルーム"
          value={stats.chatroomsCount}
          delta={{ value: '学校コーチとの会話', trend: 'steady' }}
          icon={<MessageCircle className="h-5 w-5" />}
        />
        <StatCard
          label="動画エントリー"
          value={stats.mediaEntries}
          delta={{ value: stats.mediaEntries > 0 ? '視覚的なアピールができます' : '動画を追加しましょう', trend: stats.mediaEntries > 0 ? 'up' : 'steady' }}
          icon={<PlayCircle className="h-5 w-5" />}
        />
        <StatCard
          label="承認ステータス"
          value={approvalBadge.label}
          icon={<Star className="h-5 w-5" />}
        />
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">最近のチャット</h2>
            <Link href="/player/chatrooms" className={buttonClasses({ variant: 'ghost', size: 'sm' })}>
              すべて表示
            </Link>
          </header>
          <div className="mt-6 space-y-4">
            {recentChatrooms.length === 0 ? (
              <p className="text-sm text-white/60">まだメッセージはありません。学校コーチとのチャットを開始してみましょう。</p>
            ) : (
              recentChatrooms.map((room) => (
                <Link
                  key={room.id}
                  href={`/player/chatrooms/${room.id}`}
                  className="flex items-start gap-4 rounded-2xl border border-white/5 bg-white/5 px-4 py-4 transition hover:border-brand-400/40 hover:bg-brand-500/10"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/20 text-sm font-semibold text-brand-100">
                    {(room.schoolName || room.coachName).slice(0, 2)}
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-white">{room.schoolName || '未登録の学校'}</h3>
                      <span className="text-xs text-white/60">{room.coachName}</span>
                    </div>
                    <p className="line-clamp-2 text-sm text-white/70">
                      {room.lastMessage ? room.lastMessage : 'メッセージがまだありません'}
                    </p>
                    {room.lastMessageAt ? (
                      <p className="text-xs text-white/40">
                        {new Date(room.lastMessageAt).toLocaleString('ja-JP', {
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    ) : null}
                  </div>
                </Link>
              ))
            )}
          </div>
        </Card>

        <Card className="flex flex-col gap-4">
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">プロフィールのヒント</h2>
            <Badge variant={approvalBadge.variant}>{approvalBadge.label}</Badge>
          </header>
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-white/70">次に取り組むと良い項目</p>
              {profileInsights.missingFields.length === 0 ? (
                <div className="rounded-xl border border-success-400/30 bg-success-500/10 px-4 py-3 text-sm text-success-100">
                  すべての重要項目が入力されています。この状態を維持しましょう。
                </div>
              ) : (
                <ul className="space-y-2">
                  {profileInsights.missingFields.map((field) => (
                    <li key={field} className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
                      {field}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-sm text-white/70">推奨アクション</p>
              <ul className="space-y-2">
                {profileInsights.suggestions.map((suggestion, idx) => (
                  <li key={idx} className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/70">
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
            <Link href="/player/profile/edit" className={buttonClasses({ variant: 'secondary' })}>
              未入力の項目を編集
            </Link>
            <Link href="/player/academic_grades" className={buttonClasses({ variant: 'ghost' })}>
              <GraduationCap className="h-4 w-4" />
              学業成績を入力
            </Link>
          </div>
        </Card>
      </section>
    </AppShell>
  )
}

function badgeForStatus(status: string) {
  switch (status) {
    case 'approved':
      return { label: '承認済み', variant: 'success' as const }
    case 'pending':
      return { label: '審査中', variant: 'warning' as const }
    default:
      return { label: '未提出', variant: 'outline' as const }
  }
}

function formatDate(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
